import { useState } from 'react';
import {
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import type { ChecklistItem } from '../types';
import { reorderAndResequence } from '../utils';

type UseChecklistDndProps = {
  items: ChecklistItem[];
  onReorderItems: (reorderedItems: ChecklistItem[]) => void;
};

export const useChecklistDnd = ({
  items,
  onReorderItems
}: UseChecklistDndProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const reorderedItems = reorderAndResequence(
      items,
      String(active.id),
      String(over.id)
    );
    onReorderItems(reorderedItems);
  };

  const activeItem = activeId ? items.find(item => item.id === activeId) : null;

  return {
    sensors,
    activeId,
    activeItem,
    collisionDetection: closestCenter,
    handleDragStart,
    handleDragEnd
  };
};
